import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getIndices,
  getNews,
  getQuote,
  getWatchlist,
  type IndexSnapshot,
  type MarketNews,
  type WatchlistEntry,
} from "@/api/market";
import { getSimAccount, type SimAccount } from "@/api/sim";

type DateRange = "1d" | "3d" | "7d";
type NewsTab = "holdings" | "all";

const RANGE_SECONDS: Record<DateRange, number> = { "1d": 86_400, "3d": 259_200, "7d": 604_800 };
const CATEGORIES = [
  { key: "all", label: "全部" },
  { key: "policy", label: "政策" },
  { key: "sector", label: "板块" },
  { key: "capital", label: "资金" },
  { key: "industry", label: "行业" },
  { key: "announce", label: "公告" },
];
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  policy: ["政策", "监管", "央行", "证监会"],
  sector: ["板块", "行业", "概念"],
  capital: ["资金", "北向", "主力", "机构"],
  industry: ["新能源", "半导体", "医药", "消费", "AI"],
  announce: ["公告", "财报", "业绩", "分红"],
};
const IMPORTANCE: Record<string, { color: string; label: string; order: number }> = {
  critical: { color: "bg-red-500", label: "重磅", order: 0 },
  high: { color: "bg-amber-500", label: "重要", order: 1 },
  normal: { color: "bg-blue-400", label: "", order: 2 },
  low: { color: "bg-gray-300", label: "", order: 3 },
};

function marketPhase() {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay();
  if (day === 0 || day === 6) return { label: "休市", trading: false };
  if (hour < 9 || hour >= 15) return { label: hour < 9 ? "待开盘" : "已收盘", trading: false };
  if (hour < 11 || (hour === 11 && now.getMinutes() <= 30)) return { label: "早盘", trading: true };
  if (hour < 13) return { label: "午休", trading: false };
  return { label: "午盘", trading: true };
}

function timeAgo(timestamp: number) {
  const diff = Date.now() / 1000 - timestamp;
  if (diff < 60) return "刚刚";
  if (diff < 3_600) return `${Math.floor(diff / 60)}分钟`;
  if (diff < 86_400) return `${Math.floor(diff / 3_600)}小时`;
  const date = new Date(timestamp * 1000);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function signed(value: number) { return value >= 0 ? "+" : ""; }
function changeColor(value: number) { return value >= 0 ? "text-red-500" : "text-green-500"; }
function compactNumber(value: number) {
  if (Math.abs(value) >= 1e8) return `${(value / 1e8).toFixed(2)}亿`;
  if (Math.abs(value) >= 1e4) return `${(value / 1e4).toFixed(0)}万`;
  return value.toFixed(0);
}
function normalizeCode(code: string) {
  return code.replace(/^(sh|sz|bj)/i, "").replace(/\.(SH|SZ|BJ)$/i, "");
}
function normalizeNewsTickers(tickers: MarketNews["tickers"]) {
  const values = Array.isArray(tickers) ? tickers : typeof tickers === "string" ? tickers.split(",") : [];
  return values.map((code) => normalizeCode(String(code).trim())).filter(Boolean);
}

export default function NewsHome() {
  const [allNews, setAllNews] = useState<MarketNews[]>([]);
  const [indices, setIndices] = useState<IndexSnapshot[]>([]);
  const [account, setAccount] = useState<SimAccount | null>(null);
  const [watchlist, setWatchlist] = useState<WatchlistEntry[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>("7d");
  const [category, setCategory] = useState("all");
  const [tab, setTab] = useState<NewsTab>("holdings");
  const cursorRef = useRef<number | undefined>(undefined);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasToken = Boolean(localStorage.getItem("alpha_token") || localStorage.getItem("token"));
  const phase = marketPhase();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    setHasMore(true);
    cursorRef.current = undefined;

    (async () => {
      try {
        const since = Math.floor(Date.now() / 1000) - RANGE_SECONDS[dateRange];
        const collected: MarketNews[] = [];
        let cursor: number | undefined;

        for (let page = 0; page < 5 && active; page += 1) {
          const response = await getNews(undefined, 100, cursor, since);
          const items = response.news || [];
          if (items.length === 0) break;
          collected.push(...items);
          const oldest = items.at(-1);
          const nextCursor = oldest?.publishedAt || oldest?.createdAt;
          if (!nextCursor || nextCursor === cursor || nextCursor < since) {
            cursor = nextCursor;
            break;
          }
          cursor = nextCursor;
        }

        const [indexResult, simResult, watchlistResult] = await Promise.all([
          getIndices(["000001.SH", "399001.SZ", "399006.SZ", "000688.SH", "899050.BJ"]).catch(() => ({ indices: [] })),
          hasToken ? getSimAccount().catch(() => null) : Promise.resolve(null),
          hasToken ? getWatchlist().catch(() => ({ watchlist: [] })) : Promise.resolve({ watchlist: [] }),
        ]);

        if (!active) return;
        setAllNews(collected);
        setIndices(indexResult.indices || []);
        setAccount(simResult);
        setWatchlist(watchlistResult.watchlist || []);
        cursorRef.current = cursor;
        setHasMore(collected.length >= 100 && Boolean(cursor));

        if (simResult?.positions?.length) {
          const quotes = await Promise.all(simResult.positions.map((position) => getQuote(position.ticker).catch(() => null)));
          if (active) setIndustries(quotes.map((quote) => quote?.ticker?.industry).filter(Boolean) as string[]);
        } else {
          setIndustries([]);
        }
      } catch {
        if (active) setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [dateRange, hasToken]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursorRef.current) return;
    setLoadingMore(true);
    try {
      const since = Math.floor(Date.now() / 1000) - RANGE_SECONDS[dateRange];
      const response = await getNews(undefined, 20, cursorRef.current, since);
      const items = response.news || [];
      if (items.length) {
        setAllNews((current) => [...current, ...items]);
        const oldest = items.at(-1);
        cursorRef.current = oldest?.publishedAt || oldest?.createdAt;
      }
      setHasMore(items.length >= 20 && Boolean(cursorRef.current));
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [dateRange, hasMore, loadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) void loadMore();
    }, { rootMargin: "400px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const filteredNews = useMemo(() => {
    const since = Date.now() / 1000 - RANGE_SECONDS[dateRange];
    return allNews.filter((item) => {
      if ((item.publishedAt || item.createdAt) < since) return false;
      const text = `${item.title || ""}${item.summary || ""}${item.source || ""}`;
      if (search && !text.toLowerCase().includes(search.toLowerCase())) return false;
      if (category !== "all" && !(CATEGORY_KEYWORDS[category] || []).some((keyword) => text.includes(keyword))) return false;
      return true;
    });
  }, [allNews, category, dateRange, search]);

  const holdingsNews = useMemo(() => {
    if (!hasToken) return [];
    const codes = new Set<string>();
    const names = new Set<string>();
    for (const position of account?.positions || []) {
      codes.add(normalizeCode(position.ticker));
      names.add(position.name);
    }
    for (const item of watchlist) {
      if (item.code) codes.add(normalizeCode(item.code));
      if (item.name) names.add(item.name);
    }
    return filteredNews.filter((item) => {
      const text = `${item.title || ""}${item.summary || ""}`;
      const newsCodes = normalizeNewsTickers(item.tickers);
      if (newsCodes.some((code) => codes.has(code))) return true;
      if ([...names].some((name) => name.length >= 2 && text.includes(name))) return true;
      if ([...codes].some((code) => code.length >= 6 && text.includes(code))) return true;
      return industries.some((industry) => industry.length >= 2 && text.includes(industry));
    });
  }, [account, filteredNews, hasToken, industries, watchlist]);

  const displayedNews = useMemo(() => {
    const source = tab === "holdings" ? holdingsNews : filteredNews;
    return [...source].sort((left, right) => {
      const importanceDiff = (IMPORTANCE[left.importance]?.order ?? 3) - (IMPORTANCE[right.importance]?.order ?? 3);
      return importanceDiff || (right.publishedAt || right.createdAt) - (left.publishedAt || left.createdAt);
    });
  }, [filteredNews, holdingsNews, tab]);

  if (loading) {
    return <div className="p-4 space-y-2">{[1, 2, 3, 4, 5].map((item) => <div key={item} className="h-14 bg-background-100 rounded-lg animate-pulse" />)}</div>;
  }

  return (
    <div className="pb-4">
      <header className="px-4 pt-3 pb-2 sticky top-0 bg-white/95 backdrop-blur-xl z-10 border-b border-background-100">
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-2 h-2 rounded-full ${phase.trading ? "bg-red-500 animate-pulse" : "bg-gray-300"}`} />
          <span className="text-xs text-foreground-500">{phase.label}</span>
          <h1 className="text-lg font-bold text-foreground-950 ml-1">市场新闻</h1>
          <div className="text-[10px] text-foreground-400 ml-auto hidden sm:flex gap-2">
            {indices.slice(0, 3).map((index) => (
              <span key={index.code}>{(index.name || index.code).replace("指数", "").slice(0, 2)} <b className={changeColor(index.changePct)}>{signed(index.changePct)}{index.changePct?.toFixed(1)}%</b></span>
            ))}
          </div>
        </div>

        {hasToken && account && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-background-50 rounded-lg px-3 py-1.5 text-[11px] mb-2">
            <span className="text-foreground-400">总资产 <b className="text-foreground-900">¥{compactNumber(account.totalValue)}</b></span>
            <span className={changeColor(account.totalPnL)}>盈亏 {signed(account.totalPnL)}¥{compactNumber(Math.abs(account.totalPnL))}</span>
            <span className={changeColor(account.totalReturn)}>收益率 {signed(account.totalReturn)}{account.totalReturn?.toFixed(2)}%</span>
            <span className="text-foreground-400">持仓 {account.positions?.length || 0}只</span>
          </div>
        )}

        <div className="flex items-center gap-1 bg-background-50 rounded-lg p-0.5 mb-2">
          <button onClick={() => setTab("holdings")} className={`flex-1 py-1.5 text-xs font-medium rounded-md ${tab === "holdings" ? "bg-white text-primary-500 shadow-sm" : "text-foreground-500"}`}>
            我的持仓 {holdingsNews.length > 0 && `(${holdingsNews.length})`}
          </button>
          <button onClick={() => setTab("all")} className={`flex-1 py-1.5 text-xs font-medium rounded-md ${tab === "all" ? "bg-white text-primary-500 shadow-sm" : "text-foreground-500"}`}>
            全部新闻 ({filteredNews.length})
          </button>
        </div>

        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-300 text-xs" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索新闻关键词..."
            className="w-full bg-background-50 rounded-lg pl-8 pr-8 py-1.5 text-xs outline-none border border-transparent focus:border-primary-200" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-300"><i className="ri-close-line text-xs" /></button>}
        </div>

        <div className="flex items-center gap-1.5 mt-2 overflow-x-auto no-scrollbar">
          {(["1d", "3d", "7d"] as DateRange[]).map((range) => (
            <button key={range} onClick={() => setDateRange(range)} className={`shrink-0 text-[10px] px-2 py-1 rounded-full font-medium ${dateRange === range ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>
              {range === "1d" ? "今天" : range === "3d" ? "近3天" : "近7天"}
            </button>
          ))}
          <span className="text-foreground-200">|</span>
          {CATEGORIES.map((item) => (
            <button key={item.key} onClick={() => setCategory(item.key)} className={`shrink-0 text-[10px] px-2 py-1 rounded-full font-medium ${category === item.key ? "bg-foreground-900 text-white" : "text-foreground-500 hover:bg-background-100"}`}>
              {item.label}
            </button>
          ))}
        </div>
      </header>

      <main className="px-4 mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground-900">
            {tab === "holdings" ? "与我相关" : search ? `搜索“${search}”` : "新闻列表"} ({displayedNews.length})
          </span>
        </div>

        {displayedNews.length === 0 ? (
          <div className="text-center py-10 text-xs text-foreground-400">
            {failed ? "新闻加载失败，请稍后重试" : tab === "holdings" ? "暂无与持仓相关的新闻" : search ? "没有匹配的新闻" : "暂无新闻"}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {displayedNews.map((item) => {
              const importance = IMPORTANCE[item.importance] || IMPORTANCE.normal;
              const card = (
                <>
                  <div className="flex items-center gap-1.5 mb-1">
                    {importance.label && <span className={`text-[8px] text-white px-1 py-0.5 rounded ${importance.color}`}>{importance.label}</span>}
                    <span className="text-[10px] text-foreground-400">{item.source}</span>
                    <span className="text-[10px] text-foreground-300">{timeAgo(item.publishedAt || item.createdAt)}</span>
                    {item.sentiment === "positive" && <span className="text-[9px] text-red-500 bg-red-50 px-1 rounded">利好</span>}
                    {item.sentiment === "negative" && <span className="text-[9px] text-green-500 bg-green-50 px-1 rounded">利空</span>}
                  </div>
                  <h2 className="text-xs font-medium text-foreground-900 leading-snug line-clamp-2">{item.title}</h2>
                  {(item.aiAnalysis || item.summary) && <p className="text-[10px] text-foreground-500 mt-1 line-clamp-2">{item.aiAnalysis || item.summary}</p>}
                </>
              );
              const className = "block bg-background-50 rounded-lg p-3 hover:bg-background-100 transition-colors border border-transparent hover:border-background-200";
              return item.url ? <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className={className}>{card}</a> : <article key={item.id} className={className}>{card}</article>;
            })}
          </div>
        )}
      </main>

      <div ref={sentinelRef} className="h-1" />
      {loadingMore && <div className="text-center py-3 text-xs text-foreground-400">加载中...</div>}
      {!hasMore && displayedNews.length > 0 && <div className="text-center py-3 text-xs text-foreground-300">已显示全部新闻</div>}
    </div>
  );
}
